import type { TypedWrappedFieldProps } from "redux-form";

import {
  Box,
  Stack,
  StyledTileButton,
  Typography,
  useButtonSelect,
} from "@braintrust/braintrust-ui-components";
import { shouldDisplayError } from "@js/forms/utils";

import {
  FieldSectionPanelBase,
  FieldSectionPanelWithImportanceTitle,
} from "../field-section-panel";

import styles from "./styles.module.scss";

type WorkAuthorizationFieldProps = {
  legally_authorized_to_work: TypedWrappedFieldProps<boolean | undefined>;
  visa_sponsorship_may_be_required: TypedWrappedFieldProps<boolean | undefined>;
};

export const WorkAuthorizationField = ({
  legally_authorized_to_work,
  visa_sponsorship_may_be_required,
}: WorkAuthorizationFieldProps) => {
  const isErrorOnLegallyAuthorizedToWork = shouldDisplayError(
    legally_authorized_to_work,
  );
  const isErrorOnVisaSponsorshipRequired = shouldDisplayError(
    visa_sponsorship_may_be_required,
  );
  const isError =
    isErrorOnLegallyAuthorizedToWork || isErrorOnVisaSponsorshipRequired;

  const legallyAuthorizedToWorkError = isErrorOnLegallyAuthorizedToWork
    ? legally_authorized_to_work.meta.error
    : undefined;
  const visaSponsorshipError = isErrorOnVisaSponsorshipRequired
    ? visa_sponsorship_may_be_required.meta.error
    : undefined;

  return (
    <FieldSectionPanelBase isError={isError} id="work-authorization">
      <FieldSectionPanelWithImportanceTitle
        title="Are you legally authorized to work in the country where the job is located?"
        fieldImportance="required"
        isError={isErrorOnLegallyAuthorizedToWork}
      />
      <Box sx={{ mb: 2 }}>
        <YesNoOptionTileButtons
          value={legally_authorized_to_work.input.value}
          onChange={legally_authorized_to_work.input.onChange}
          id="legally-authorized-to-work"
        />
        {!!legallyAuthorizedToWorkError && (
          <Typography
            component="p"
            error
            size="medium"
            variant="paragraph"
            sx={{ mt: 2 }}
          >
            {legallyAuthorizedToWorkError}
          </Typography>
        )}
      </Box>
      <FieldSectionPanelWithImportanceTitle
        title="Will you now or in the future require visa sponsorship to work in the country where the job is located?"
        fieldImportance="required"
        isError={isErrorOnVisaSponsorshipRequired}
      />
      <YesNoOptionTileButtons
        value={visa_sponsorship_may_be_required.input.value}
        onChange={visa_sponsorship_may_be_required.input.onChange}
        id="visa-sponsorship-may-be-required"
      />
      {!!visaSponsorshipError && (
        <Typography
          component="p"
          error
          size="medium"
          variant="paragraph"
          sx={{ mt: 2 }}
        >
          {visaSponsorshipError}
        </Typography>
      )}
    </FieldSectionPanelBase>
  );
};

const YES_OR_NO_OPTIONS = [
  { value: true, label: "Yes" },
  { value: false, label: "No" },
];

const YesNoOptionTileButtons = ({
  value,
  onChange,
  id,
}: {
  value: boolean | undefined;
  onChange: (value: boolean | undefined | null) => void;
  id: string;
}) => {
  const { isSelected, handleChange, containerA11yProps, itemA11yProps } =
    useButtonSelect({
      value,
      onChange: (newValue) => onChange(newValue),
      canSingleValueBeEmpty: false,
    });

  return (
    <Stack
      direction="row"
      {...containerA11yProps}
      id={id}
      data-testid={id}
      sx={{ gap: 1 }}
    >
      {YES_OR_NO_OPTIONS.map((option) => {
        const isOptionSelected = isSelected(option);

        return (
          <Box key={option.label} className={styles.tile}>
            <StyledTileButton
              key={option.label}
              {...itemA11yProps(isOptionSelected)}
              variant="white-violet"
              shape="squared"
              isSelected={isOptionSelected}
              onClick={(ev) => {
                ev.preventDefault();
                handleChange(option);
              }}
            >
              {option.label}
            </StyledTileButton>
          </Box>
        );
      })}
    </Stack>
  );
};
